<!--
 @description Game rules page. Lists the nine gameplay rules as an ordered
 list with locale-aware content (French-only text-2 is conditionally shown).
 Includes a locale-aware PWA install link and a link to the disclaimer.
 CTA buttons adapt to auth state: unauthenticated users are sent to auth,
 authenticated users to the game menu. Buttons are hidden when rendered
 inside a modal via the `inModal` prop.

 @example
 Route: /rules (name: 'rules')
 <Rules />
 Embedded in a modal:
 <Rules :inModal="true" />
-->
<template>
  <div class="rules-container">
    <img
      src="/avatars/rules/avatars-high-five.svg"
      alt="Avatars high five"
      class="rules__avatars"
      v-if="!inModal"
    />
    <div class="rules__content" :class="{ 'rules__content--no-background': inModal }">
      <h2 class="primary">{{ $t('pages.rules.title') }}</h2>
      <p>{{ $t('pages.rules.text-1') }}</p>
      <p v-if="locale === 'fr'">{{ $t('pages.rules.text-2') }}</p>
      <ol>
        <li>{{ $t('pages.rules.rule-1') }}</li>
        <li>{{ $t('pages.rules.rule-2') }}</li>
        <li>{{ $t('pages.rules.rule-3') }}</li>
        <li>{{ $t('pages.rules.rule-4') }}</li>
        <li>{{ $t('pages.rules.rule-5') }}</li>
        <li>{{ $t('pages.rules.rule-6') }}</li>
        <li>{{ $t('pages.rules.rule-7') }}</li>
        <li>{{ $t('pages.rules.rule-8') }}</li>
        <li>
          {{ $t('pages.rules.rule-9-a') }}
          <a
            :href="locale === 'fr' ? HOW_TO_INSTALL_LINK_FR : HOW_TO_INSTALL_LINK_EN"
            aria-label="How to install"
            class="regular-link"
            target="_blank"
            >{{ $t('pages.rules.rule-9-b') }}</a
          >
        </li>
      </ol>
      <p>{{ $t('pages.rules.text-3') }}</p>

      <a
        :href="locale === 'fr' ? HOW_TO_INSTALL_LINK_FR : HOW_TO_INSTALL_LINK_EN"
        aria-label="How to install"
        class="rules__install"
        target="_blank"
        >{{ $t('pages.rules.how-to-install') }}
        <i class="fi fi-rr-arrow-up-right-from-square"></i>
      </a>

      <router-link :to="{ name: 'disclaimer' }" aria-label="Disclaimer">{{
        $t('pages.rules.disclaimer')
      }}</router-link>

      <button
        type="button"
        class="secondary"
        v-show="!isSignedIn"
        v-if="!inModal"
        @click="router.push({ name: 'auth' })"
      >
        {{ $t('pages.auth.menu.sign-up') }}
      </button>
      <button
        type="button"
        class="secondary bold"
        v-show="isSignedIn"
        v-if="!inModal"
        @click="router.push({ name: 'games' })"
      >
        {{ $t('pages.home.play-game') }}
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { HOW_TO_INSTALL_LINK_FR, HOW_TO_INSTALL_LINK_EN } from '@/types/Constants'
import { useAuthState } from '@/composables/useAuthState'

const { locale } = useI18n()

const router = useRouter()
const { isSignedIn } = useAuthState()

/** Props for the Rules view. */
interface RulesProps {
  /** When true, hides the sign-up/play CTA buttons and avatar illustration —
   *  use when rendering this view as modal content. */
  inModal?: boolean
}

defineProps<RulesProps>()
</script>

<style scoped lang="scss">
.rules-container {
  position: relative;
  height: fit-content;
  width: 100vw;
  padding: 11rem 0 5rem;
  margin: 0;
  margin-bottom: 6rem;
  overflow: hidden;

  @include breakpoint-md {
    min-height: auto;
  }
}

.rules__content {
  position: relative;
  z-index: 2;
  background-color: var(--white);
  height: fit-content;
  width: calc(100vw - 2rem);
  max-width: 600px;
  margin: 0 auto;
  padding: $triple-spacing $base-spacing;
  @extend %flex-column;
  justify-content: flex-start;
  gap: $half-spacing;
  clip-path: polygon(10% 0, 90% 0, 100% 5%, 100% 95%, 90% 100%, 10% 100%, 0 95%, 0 5%);

  &--no-background {
    background-color: transparent;
    clip-path: none;
  }

  h2 {
    padding: $half-spacing 0;
    margin: 0;
  }

  p {
    margin: $half-spacing 0;
  }

  p,
  li {
    font-size: $font-size-sm;
    padding: 0 $base-spacing;
    text-align: center;
    line-height: normal;

    @include min-breakpoint-md {
      font-size: $base-font-size;
    }
  }

  li {
    font-size: $font-size-sm;
    text-align: left;
    padding: $half-spacing 1.5rem;

    a {
      font-size: $font-size-sm;
    }

    @include min-breakpoint-md {
      font-size: $base-font-size;

      a {
        font-size: $base-font-size;
      }
    }
  }

  a {
    margin-top: 0;
    text-decoration: none;
    text-align: center;
    color: var(--primary);
    border: 0px !important;
    padding-bottom: 0px !important;

    &:hover {
      text-decoration: underline;
    }

    @include breakpoint-md {
      text-decoration: underline;
    }
  }

  button {
    margin-top: $base-spacing;
    transform: scale(1.3);
    width: 30%;

    @include breakpoint-md {
      width: 55%;
      transform: scale(0.9);
    }
  }
}

.rules__install {
  margin-top: $double-spacing;
}

.rules__avatars {
  position: absolute;
  z-index: 1;
  top: 30px;
  left: 50%;
  transform: translateX(-50%);
  width: 220px;
}
</style>
